import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Stethoscope, Trash2, Loader2, MapPin } from "lucide-react";

const TESTS = ["PrevenTest", "NewbornGeneID", "ProstateGeneID", "Tumor Testing"];
const EMPTY = { physician_name: "", practice_name: "", specialty: "", city: "", state: "", email: "", phone: "", test_interest: "", notes: "" };

export default function Physicians() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/physicians").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/physicians", form);
      toast.success("Physician onboarded — overrides distributed to your upline.");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/physicians/${id}`);
      toast.success("Referral removed.");
      load();
    } catch (err) {
      toast.error("Could not remove referral.");
    }
  };

  return (
    <div data-testid="physicians-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Referrals</p>
          <h1 className="font-display text-3xl text-navy mt-1">My Physicians</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-physician-btn" className="rounded-full bg-patriot hover:bg-patriot-dark text-white gap-2">
              <Plus className="h-4 w-4" /> Onboard Physician
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-navy">Onboard a Physician</DialogTitle>
              <DialogDescription>Add a referral physician. Overrides are distributed across your six-level upline.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="physician-form">
              <div>
                <Label className="text-navy">Physician name *</Label>
                <Input required value={form.physician_name} onChange={set("physician_name")} data-testid="physician-name" className="mt-1.5" placeholder="Dr. Jane Smith" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-navy">Practice</Label><Input value={form.practice_name} onChange={set("practice_name")} data-testid="physician-practice" className="mt-1.5" /></div>
                <div><Label className="text-navy">Specialty</Label><Input value={form.specialty} onChange={set("specialty")} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-navy">City</Label><Input value={form.city} onChange={set("city")} className="mt-1.5" /></div>
                <div><Label className="text-navy">State</Label><Input value={form.state} onChange={set("state")} className="mt-1.5" /></div>
              </div>
              <div>
                <Label className="text-navy">Test interest</Label>
                <Select value={form.test_interest} onValueChange={(v) => setForm({ ...form, test_interest: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="physician-test"><SelectValue placeholder="Select a test" /></SelectTrigger>
                  <SelectContent>{TESTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-navy">Notes</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1.5" rows={2} /></div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="physician-submit" className="rounded-full bg-navy hover:bg-navy-dark text-white w-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Distribute Overrides"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {list === null ? (
        <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-xl p-16 text-center" data-testid="physicians-empty">
          <Stethoscope className="h-12 w-12 text-navy/20 mx-auto mb-4" />
          <h3 className="font-display text-xl text-navy">No physicians yet</h3>
          <p className="text-muted-foreground mt-2 max-w-sm mx-auto">Onboard your first physician to start earning $500 overrides across your team.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="physicians-list">
          {list.map((p) => (
            <div key={p.id} className="bg-white border border-border rounded-xl p-6 group hover:border-navy transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-xl text-navy">{p.physician_name}</h3>
                  {p.practice_name && <p className="text-sm text-muted-foreground">{p.practice_name}</p>}
                </div>
                <button onClick={() => remove(p.id)} data-testid={`delete-physician-${p.id}`} className="text-muted-foreground hover:text-patriot transition-colors opacity-0 group-hover:opacity-100">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.test_interest && <span className="text-xs font-semibold text-patriot bg-patriot/8 rounded-full px-3 py-1">{p.test_interest}</span>}
                {p.specialty && <span className="text-xs text-muted-foreground bg-muted rounded-full px-3 py-1">{p.specialty}</span>}
              </div>
              {(p.city || p.state) && (
                <p className="mt-4 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> {[p.city, p.state].filter(Boolean).join(", ")}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
